import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';

import { AllUserMonarxDetails200RowsItem } from '@newfold/huapi-js/src/index.schemas';

import { ReactComponent as InfoIcon } from '~/assets/images/info-icon.svg';
import {
  CardActionType,
  SEVERITY_COLORS,
} from '~/components/CardTopBanner/utils/constants';
import { CardAction } from '~/components/CardTopBanner/utils/types';
import { useMFEContext } from '~/components/MFEProvider/lib/MFEProvider';
import { REIMAGE_IN_PROGRESS_STATUSES } from '~/Nova/scenes/HostingList/scenes/SelfManagedHosting/utils/constants';
import { getSelfManagedPlanName } from '~/Nova/scenes/HostingList/utils/helpers';
import OverviewTopCap from '~/Nova/scenes/Site/scenes/Overview/components/OverviewTopCap';
import { getHostingRenewalStatus } from '~/scenes/Hosting/components/RenewalSettings/utils/helpers';
import {
  HEALTH_STATUS_INFECTED,
  MonarxPlan,
} from '~/scenes/Site/scenes/Security/utils/constants';
import { YYYY_MMMM_DD_HMS } from '~/utils/date/constants';
import {
  DECOM_STATES,
  DELETED_STATES,
  FAILED_STATES,
  PENDING_STATES,
  SUSPENDED_STATES,
} from '~/utils/hosting/constants';

import { useBrandInfo } from './useBrandInfo';

const isMonarxAlertShown = (
  data: AllUserMonarxDetails200RowsItem | undefined,
): boolean => {
  if (!data) return false;
  const plan = data.plan?.toLowerCase() as MonarxPlan;
  const health = data.health_status?.toLowerCase() ?? '';
  return (
    Object.values(MonarxPlan).includes(plan) &&
    health === HEALTH_STATUS_INFECTED
  );
};

export interface InfoBannerInput {
  status: string | null;
  expirationDate?: string | null;
  autoRenewFlag: boolean | null;
  diskUsedPercentage: number | null;
  isSiteLimitReached: boolean;
  onUpgradeClick: () => void;
  startServer?: () => void;
  canUpgrade?: boolean;
  ctbData?: {
    prodInstId: string | null;
    productName?: string | null;
    parentProdInstId?: string | null;
  };
  serverStatus: string | null | undefined;
  isAdvanced: boolean;
  isSelfManagedVPS: boolean;
  isSelfManagedDedi?: boolean;
  isHosting_01s: boolean;
  isFreeTrialAiHosting?: boolean;
  isHostingPlanReady: boolean;
  monarxDashboardData?: AllUserMonarxDetails200RowsItem;
  isListPage?: boolean;
}

export type BannerSeverity = 'error' | 'warning' | 'info' | 'custom';

export interface BannerConfig {
  key: string;
  severity: BannerSeverity;
  icon?: React.ReactNode;
  title: string;
  description?: React.ReactNode;
  accentColor: string;
  backgroundColor: string;
  borderColor?: string;
  actions?: CardAction[];
  ctbData?: { prodInstId: string | null; productName?: string | null };
  hide?: boolean;
  render?: React.ReactNode;
}

export const useInfoBanners = ({
  status,
  expirationDate = '',
  autoRenewFlag = false,
  diskUsedPercentage,
  isSiteLimitReached,
  onUpgradeClick,
  canUpgrade = false,
  ctbData,
  serverStatus,
  startServer,
  isAdvanced,
  isSelfManagedVPS,
  isSelfManagedDedi,
  isHosting_01s,
  isFreeTrialAiHosting,
  isHostingPlanReady,
  monarxDashboardData,
  isListPage = false,
}: InfoBannerInput): BannerConfig[] => {
  const { t } = useTranslation('hosting', { keyPrefix: 'hostingNova.banner' });
  const {
    hostingId,
    data: { renewalCenterLink },
    callbacks: { handleClickToBuy },
  } = useMFEContext() as any;

  const banners: BannerConfig[] = [];
  const { contactLink, renewalCenterLink: brandRenewalCenterLink } =
    useBrandInfo();
  const renewLink = brandRenewalCenterLink || renewalCenterLink;

  // Free trial SKUs should never show any top-cap banners.
  if (isFreeTrialAiHosting) {
    return banners;
  }

  const selfManagedPlanType = getSelfManagedPlanName(!!isSelfManagedDedi);

  // Monarx alert (OverviewTopCap) - when applicable
  if (monarxDashboardData && isMonarxAlertShown(monarxDashboardData)) {
    banners.push({
      key: 'monarx-alert',
      severity: 'custom',
      title: '',
      accentColor: '',
      backgroundColor: '',
      render: (
        <OverviewTopCap
          hostingId={hostingId}
          monarxDashboardData={monarxDashboardData}
          isListPage={isListPage}
        />
      ),
    });
  }

  const { isExpired, willExpireSoon, willExpireInDays } =
    getHostingRenewalStatus(expirationDate ?? '', YYYY_MMMM_DD_HMS);
  const expiredOrDecom = isExpired || DECOM_STATES.includes(status ?? '');

  const handleRenew = async () => {
    if (handleClickToBuy?.HOSTING_RENEW) {
      await handleClickToBuy.HOSTING_RENEW();
      return;
    }
    if (renewLink) window.location.assign(renewLink);
  };

  const handleContactSupport = () => {
    window.open(contactLink, '_blank');
  };

  const handleStartServer = () => startServer?.();

  // Account FAILED banners
  if (FAILED_STATES.includes(status ?? '')) {
    banners.push({
      key: 'failed',
      severity: 'error',
      title: t('accountFailed.title'),
      ...SEVERITY_COLORS.error,
      actions: [
        {
          label: t('actions.contactSupport'),
          onClick: handleContactSupport,
          variant: 'outlined',
        },
      ],
    });
  }
  // Account DELETED banners
  if (DELETED_STATES.includes(status ?? '')) {
    banners.push({
      key: 'deleted',
      severity: 'error',
      title: t('accountDeleted.title'),
      ...SEVERITY_COLORS.error,
      actions: [
        {
          label: t('actions.contactSupport'),
          onClick: handleContactSupport,
          variant: 'outlined',
        },
      ],
    });
  }

  // Account PENDING banners
  const isPendingState = PENDING_STATES.includes(status ?? '');
  const shouldShowPendingBanner =
    isPendingState || (isSelfManagedVPS && !isHostingPlanReady);
  if (shouldShowPendingBanner) {
    banners.push({
      key: 'pending',
      severity: 'info',
      title: isSelfManagedVPS
        ? t('vpsProvisioning.title', {
            hostingPlanType: selfManagedPlanType,
          })
        : t('accountPending.title'),
      icon: (
        <Box
          display="flex"
          alignItems="center"
          color={SEVERITY_COLORS.info.accentColor}
        >
          <InfoIcon style={{ width: 24, height: 24 }} />
        </Box>
      ),
      ...SEVERITY_COLORS.info,
    });
  }

  const isSuspendedState = SUSPENDED_STATES.includes(status ?? '');
  const shouldHideSuspendedBanner = Boolean(expiredOrDecom);
  if (isSuspendedState && !shouldHideSuspendedBanner) {
    banners.push({
      key: 'suspended',
      severity: 'error',
      title: t('accountSuspended.title'),
      ...SEVERITY_COLORS.error,
      actions: [
        {
          label: t('actions.contactSupport'),
          onClick: handleContactSupport,
          variant: 'outlined',
        },
      ],
    });
  }

  // Account expired
  if (expiredOrDecom) {
    const title = t('accountExpired.title');
    const btnLabel = t('actions.renewNow');

    banners.push({
      key: 'expired',
      severity: 'error',
      title,
      ...SEVERITY_COLORS.error,
      actions: [
        {
          label: btnLabel,
          onClick: handleRenew,
          variant: 'outlined',
        },
      ],
    });
  }

  // Account will expire soon
  if (willExpireSoon && !autoRenewFlag) {
    const title = t('accountWillExpire.title', {
      expireDays: willExpireInDays,
    });
    const btnLabel = t('actions.renewNow');
    banners.push({
      key: 'will-expire',
      severity: 'warning',
      title,
      ...SEVERITY_COLORS.warning,
      actions: [
        {
          label: btnLabel,
          onClick: handleRenew,
          variant: 'outlined',
        },
      ],
    });
  }

  // Website limit reached
  if (
    status === 'active' &&
    isSiteLimitReached &&
    canUpgrade &&
    !isHosting_01s
  ) {
    banners.push({
      key: 'site-limit-reached',
      severity: 'warning',
      title: t('siteLimit.title'),
      ...SEVERITY_COLORS.warning,
      ctbData,
      actions: [
        {
          label: t('actions.hostingUpgrade'),
          onClick: onUpgradeClick, // Parent should set state to open CTB
          variant: 'outlined',
          type: CardActionType.HOSTING_UPGRADE,
        },
      ],
    });
  }

  // Disk usage error - when usage is above 80% and user can upgrade
  if (
    status === 'active' &&
    diskUsedPercentage &&
    diskUsedPercentage >= 80 &&
    canUpgrade
  ) {
    banners.push({
      key: 'disk-usage-error',
      severity: 'error',
      title: t('diskUsageWarning.title', {
        percentage: diskUsedPercentage,
      }),
      ...SEVERITY_COLORS.error,
      ctbData,
      actions: [
        {
          label: t('actions.hostingUpgrade'),
          onClick: onUpgradeClick, // Parent should set state to open CTB
          variant: 'outlined',
          type: CardActionType.HOSTING_UPGRADE,
        },
      ],
    });
  }

  //vps - self-managed-reimaging
  if (
    REIMAGE_IN_PROGRESS_STATUSES.includes(serverStatus || '') &&
    isSelfManagedVPS
  ) {
    banners.push({
      key: 'reimaging-in-progress',
      severity: 'info',
      title: t('reImaging.title', {
        hostingPlanType: selfManagedPlanType,
      }),
      ...SEVERITY_COLORS.info,
      ctbData,
    });
  }

  // vps - server offline
  if (isAdvanced && status === 'active' && serverStatus === 'stopped') {
    banners.push({
      key: 'server-offline',
      severity: 'warning',
      title: t('serverOffline.title'),
      ...SEVERITY_COLORS.warning,
      ctbData,
      actions: [
        {
          label: t('actions.startServer'),
          onClick: handleStartServer,
          variant: 'outlined',
        },
      ],
    });
  }

  return banners;
};
